"use client";

import React, { Suspense, useState, use, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";

type PersistStreamedServerComponentRendererProps = {
  /** The promise that resolves to the component to be rendered if `persistCache` is false. */
  renderedComponentPromise: Promise<React.ReactNode>;
  /** The skeleton to be displayed while the component is being fetched. */
  loadingSkeleton: React.ReactNode | undefined;
  /** A unique key to identify the suspense boundary. */
  suspenseKey?: string;
};

/**
 * ClientCachedStreamedServerComponentRenderer is a React functional component that conditionally renders
 * either a cached component or a component fetched from a promise, based on the `persistCache` prop.
 *
 * <ClientCachedStreamedServerComponentRenderer
 *   renderedComponentPromise={yourComponentPromise}
 *   loadingSkeleton={<LoadingSkeleton />}
 *   suspenseKey="unique-key"
 * />
 */

export const PersistStreamedServerComponentRenderer: React.FC<
  PersistStreamedServerComponentRendererProps
> = ({ renderedComponentPromise, loadingSkeleton, suspenseKey }) => {
  const [savedComponent, setSavedComponent] = useState<
    React.ReactNode | undefined
  >(undefined);

  useEffect(() => {
    renderedComponentPromise.then((component) => {
      setSavedComponent(component);
    });
  }, [renderedComponentPromise]);

  return (
    <ErrorBoundary fallback={<>{savedComponent || loadingSkeleton}</>}>
      <Suspense fallback={savedComponent || loadingSkeleton} key={suspenseKey}>
        <WithPromise renderedComponentPromise={renderedComponentPromise} />
      </Suspense>
    </ErrorBoundary>
  );
};

const WithPromise: React.FC<{
  renderedComponentPromise: Promise<React.ReactNode>;
}> = ({ renderedComponentPromise }) => {
  const component = use(renderedComponentPromise);
  return <>{component}</>;
};
